import React from 'react';
import SparkChatBubbleInner from './SparkChatBubbleInner';
import ChatHistoryExpandedView from './ChatHistoryExpandedView';
import { useDialog } from 'hooks/common';
import { useBlobStorage } from 'hooks/useBlobStorage';
import { useAddChatMessage } from 'hooks/chats/useAddChatMessage';
import { useUpdateChatMessage } from 'hooks/chats/useUpdateChatMessage';
import { useFileUploads } from 'hooks/useFileUploads';
import type { FileRejection } from 'react-dropzone';
import type { FooterActions } from './ChatMessageBubble';
import { Box } from '@mui/material';
import StreamingMessageBubble from './StreamingMessageBubble';
import { AttachmentCardType } from './AttachmentCard';
import { useWorkspaceStore } from 'stores';
import { useQueryClient } from 'react-query';
import { getChatMessagesQueryKey } from 'helpers/chats';
import { getChatMessagesPage } from 'hooks/chats/useInfiniteChatMessages';
import { SPARK_RESOURCE_TYPES } from 'constants/common';
import { getChatMessageRatingTypes } from 'hooks/EntityTypes/useChatMessageRatingTypes';
import { v4 } from 'uuid';
import { useGetBlobSasToken } from 'hooks/useGetBlobSasToken';

export type SparkChatBubbleProps = {
  additionalAttachments?: AttachmentCardType[];
  onAttachmentRemove?: (id: AttachmentCardType['id']) => void;
  chatID: string;
  type?: 'board' | 'chat';
  boardID?: string;
  className?: string;
  handleBoardEvent?: (events: string) => void;
  isBoardNodeCollapsedView?: boolean;
};

function SparkChatBubble({
  additionalAttachments = [],
  chatID,
  type = 'chat',
  boardID,
  onAttachmentRemove,
  className,
  handleBoardEvent,
  isBoardNodeCollapsedView = false,
}: SparkChatBubbleProps) {
  const { workspace } = useWorkspaceStore();
  const { mutateAsync: addMessage, streamingMessage } = useAddChatMessage(
    chatID,
    type,
    handleBoardEvent
  );
  const queryClient = useQueryClient();
  const { mutateAsync: uploadFiles } = useFileUploads();
  const [attachments, setAttachments] = React.useState<AttachmentCardType[]>(
    []
  );
  const { mutateAsync: updateChatMessage } = useUpdateChatMessage(
    undefined,
    chatID
  );

  const isBoardChat = React.useMemo(() => {
    return type === 'board';
  }, [type]);

  //Preload chats
  React.useEffect(() => {
    if (chatID) {
      queryClient.prefetchInfiniteQuery(
        getChatMessagesQueryKey(chatID, 10),
        ({ pageParam = 1 }) => getChatMessagesPage(chatID, pageParam, 10)
      );
      queryClient.prefetchQuery(
        SPARK_RESOURCE_TYPES.CHAT_MESSAGE_RATING_TYPES,
        getChatMessageRatingTypes
      );
    }
  }, [chatID, queryClient]);

  // Update attachments whenever additionalAttachments or fileAttachments change
  React.useEffect(() => {
    // Keep only uploaded files (non-boardNode) when additionalAttachments is empty
    if (additionalAttachments.length === 0) {
      setAttachments((prev) =>
        prev.filter((attachment) => attachment.cardType !== 'boardNode')
      );
      return;
    }

    // Get existing uploaded attachments (non-boardNode)
    const uploadedAttachments = attachments.filter(
      (attachment) => attachment.cardType !== 'boardNode'
    );

    // Combine uploaded attachments with additionalAttachments
    setAttachments([...uploadedAttachments, ...additionalAttachments]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [additionalAttachments]); // Only depend on additionalAttachments to avoid loops

  const handleAddAttachments = (attachments: AttachmentCardType) => {
    setAttachments((prev) => {
      // Check if attachment with same id already exists
      const exists = prev.some(
        (attachment) => attachment.id === attachments.id
      );
      if (exists) return prev;
      return [...prev, attachments];
    });
  };

  const { handleFileUpload } = useBlobStorage(
    workspace?.attributes.containerName || 'app-assets'
  );
  const { refetch } = useGetBlobSasToken(
    workspace?.attributes.containerName || ''
  );

  const handleSuccessfulUpload = async (
    acceptedFiles: File[],
    fileRejections: FileRejection[]
  ) => {
    // Create initial attachments with loading state
    const mappedAcceptedFiles: AttachmentCardType[] = acceptedFiles.map(
      (file) => {
        const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';

        return {
          id: file.name, // Using temporary ID based on filename
          title: file.name,
          fileTypeTag: fileExtension,
          file: file,
          details: 'Document',
          isUploading: true,
          cardType: 'file',
          thumbnail: '',
          actionKey: file.name,
        };
      }
    );

    // Add new attachments with loading state
    setAttachments((prev) => [...prev, ...mappedAcceptedFiles]);

    try {
      // Upload to blob storage
      const result = await handleFileUpload(acceptedFiles, fileRejections);
      if (!result?.length) return;
      const { data } = await refetch();
      const URLs = result?.map(
        (upload) => `${upload.url}?${data?.data?.attributes?.token}`
      );
      const fileNameMap = result?.reduce<Record<string, string>>(
        (acc, upload) => {
          return { ...acc, [upload.file.name]: upload.fileName };
        },
        {}
      );
      // Register with fileUploads endpoint
      const uploadResponse = await uploadFiles({
        chatID: chatID,
        files: URLs.map((fileUrl) => ({ url: fileUrl })),
      });
      const files = uploadResponse.data.attributes.files;

      // Update attachments with final data
      setAttachments((prev) => {
        return prev.map((attachment) => {
          // Find matching uploaded file
          const uploadedFile = files.find(
            (file) => file.fileName === fileNameMap[attachment.title]
          );

          if (uploadedFile) {
            return {
              ...attachment,
              id: uploadedFile.id || '',
              isUploading: false,
              url: uploadedFile.tempBLOBURL || uploadedFile.url,
            };
          }
          return attachment;
        });
      });

      return URLs;
    } catch (error) {
      // Handle error by updating failed attachments
      setAttachments((prev) =>
        prev.map((attachment) =>
          mappedAcceptedFiles.some((file) => file.id === attachment.id)
            ? { ...attachment, isUploading: false, error: true }
            : attachment
        )
      );
      console.error('Upload failed:', error);
    }
  };

  const handleAttachmentRemove = (
    cardType: AttachmentCardType['cardType'],
    actionKey: string
  ) => {
    console.log('actionKey', cardType, actionKey);
    setAttachments((prev) =>
      prev.filter((attachment) => attachment.id !== actionKey)
    );
    if (cardType === 'boardNode') {
      onAttachmentRemove && onAttachmentRemove(actionKey);
    }
  };
  const {
    open: isExpandedViewOpen,
    openDialog: openExpandedView,
    closeDialog: closeExpandedView,
  } = useDialog();

  const handleMessageSend = async (message: string) => {
    const chatMessageID = v4();
    await addMessage({
      id: chatMessageID,
      content: message,
      boardID,
      items: attachments.map((item) => ({
        type: item.cardType,
        id: item.id,
      })),
    });
  };

  const handleActionClick = (
    type: FooterActions,
    messageID: string,
    secondaryID?: string
  ) => {
    updateChatMessage({
      messageID,
      chatMessageRatingTypeID: Number(secondaryID),
    });
    switch (type) {
      case 'like':
        updateChatMessage({
          messageID,
          chatMessageRatingTypeID: Number(secondaryID),
        });
        break;
      case 'dislike':
        updateChatMessage({
          messageID,
          chatMessageRatingTypeID: Number(secondaryID),
        });
        break;
      case 'duplicate':
        break;
    }
  };

  return (
    <Box
      position='relative'
      width='100%'
      justifyItems='center'
      className={className}
    >
      {!isBoardNodeCollapsedView && (
        <SparkChatBubbleInner
          attachments={attachments}
          onSendMessage={handleMessageSend}
          onFileUpload={handleSuccessfulUpload}
          onAttachmentRemove={handleAttachmentRemove}
          openExpandedView={openExpandedView}
          onAddAttachments={handleAddAttachments}
          chatID={chatID}
        />
      )}
      {!isExpandedViewOpen && (
        <StreamingMessageBubble
          streamingMessage={streamingMessage}
          isBoardChat={isBoardChat}
        />
      )}
      {isExpandedViewOpen && (
        <ChatHistoryExpandedView
          chatID={chatID}
          onFileUpload={handleSuccessfulUpload}
          onSendMessage={handleMessageSend}
          onClose={closeExpandedView}
          open={isExpandedViewOpen}
          onActionClick={handleActionClick}
          attachments={attachments}
          isExpandedViewOpen={isExpandedViewOpen}
          onAttachmentRemove={handleAttachmentRemove}
          onAddAttachments={handleAddAttachments}
        />
      )}
    </Box>
  );
}

export default React.memo(SparkChatBubble);
